import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { DataTypeComponent } from '../../../data-type-component';
import { FieldConfigDTO, MoneyDTO } from '../../../../../../system/system-dtos';
import { CurrencyDTO } from '../../../lottomart/model/lottomart-model';
import { BlastV2Service } from '../../../../../views/main-table-v2/service/blast-v2.service';
import { MoneyService } from '../../money/money.service';
import { isDefined, isNotEmpty } from '../../../../../utils/utils';

@Component({
  selector: 'app-form-field-money-new',
  templateUrl: './new-money-form-field.component.html',
  styleUrls: ['./new-money-form-field.component.scss'],
  standalone: false,
})
export class NewMoneyFormFieldComponent extends DataTypeComponent implements OnDestroy, OnInit {
  @Input() formGroup: UntypedFormGroup;

  @Input() data: MoneyDTO;

  @Input() fieldConfig: FieldConfigDTO;

  @Input() editMode: boolean;

  symbol: string;

  displayMoneyValue: string = '';

  displayCurrencyCode: string = '';

  previousInternalFormGroupValue: any;

  internalFormGroup: UntypedFormGroup;

  mainRegex: RegExp = /^-?\d*$/;

  smallRegex: RegExp = /^\d{2}$/;

  editReadOnly: boolean;

  currencies: CurrencyDTO[];

  currency: CurrencyDTO;

  numberOfDecimalPlaces = 2;

  constructor(
    private moneyService: MoneyService,
    private domSanitizer: DomSanitizer,
    private changeDetectorRef: ChangeDetectorRef,
    private formBuilder: UntypedFormBuilder,
    private blastService: BlastV2Service
  ) {
    super();
  }

  transform(unicode: string): SafeHtml {
    if (unicode === undefined) {
      return '';
    }
    return this.domSanitizer.bypassSecurityTrustHtml(unicode);
  }

  ngOnDestroy() {}

  ngOnInit(): void {
    this.blastService.getCollectionObservable('currency').subscribe((currencies: CurrencyDTO[]) => {
      if (isDefined(currencies)) {
        this.currencies = currencies.sort((a: CurrencyDTO, b: CurrencyDTO) => {
          if (a.code > b.code) {
            return 1;
          }
          if (a.code < b.code) {
            return -1;
          }
          return 0;
        });
        this.initialize(this.formGroup ? this.formGroup.get(this.fieldConfig.name).value : this.data);

        this.changeDetectorRef.detectChanges();
      }
    });

    if (this.formGroup) {
      const formControl = this.formGroup.get(this.fieldConfig.name);
      formControl.valueChanges.subscribe((d) => {
        if (!this.areEqual(d, this.data)) {
          this.initialize(d);
        }
      });
      this.editReadOnly = this.formGroup.get(this.fieldConfig.name).disabled;
    }
    // this.initialize(this.formGroup ? this.formGroup.get(this.fieldConfig.name).value : this.data);
  }

  private areEqual(a: MoneyDTO, b: MoneyDTO): boolean {
    if ((a && !b) || (!a && b) || (!a.currency && b.currency) || (a.currency && !b.currency)) {
      return false;
    }
    return a.currency.code === b.currency.code && a.value === b.value;
  }

  private initialize(d: MoneyDTO) {
    this.data = d;
    if (!this.data) {
      this.data = {
        currency: null,
        value: null,
      };
    }

    if (this.data) {
      if (this.data.currency || (this.data as any).currencyCode) {
        if (!this.data.currency) {
          this.data.currency = this.blastService
            .getCollection('currency')
            .find((c) => c.code === (this.data as any).currencyCode);
        }

        if (this.data !== undefined && this.data.currency !== undefined) {
          this.symbol = this.data.currency.symbol;
        }
      }
      try {
        if (this.data.currency !== undefined && this.data.currency.decimalPlaces !== undefined) {
          this.numberOfDecimalPlaces = this.data.currency.decimalPlaces;
          this.smallRegex = new RegExp(`^[0-9]{${this.numberOfDecimalPlaces}}$`);
        }
      } catch (err) {
        // ignore
      }

      this.displayMoneyValue = this.moneyService.formatMoney(this.data, this.fieldConfig.moneyFormat);
      this.changeDetectorRef.detectChanges();
    }

    let valueMain = this.getValueMain();

    valueMain = this.data.value > -100 && this.data.value < 0 ? '-0' : valueMain;

    this.internalFormGroup = this.formBuilder.group({
      currency: this.data.currency,
      valueMain,
      valueSmall: this.data.value ? `0${String(Math.abs(this.data.value % 100))}`.slice(-2) : null,
    });
    this.previousInternalFormGroupValue = this.internalFormGroup.getRawValue();

    this.internalFormGroup.valueChanges.subscribe((d) => {
      let { valueSmall } = d;

      if (d.currency !== undefined && d.currency.decimalPlaces !== undefined) {
        this.numberOfDecimalPlaces = d.currency.decimalPlaces;
        this.smallRegex = new RegExp(`^[0-9]{${this.numberOfDecimalPlaces}}$`);
      }

      if (d.valueSmall && String(d.valueSmall).length > this.numberOfDecimalPlaces) {
        valueSmall = String(d.valueSmall).slice(0, this.numberOfDecimalPlaces);
        this.internalFormGroup.get('valueSmall').setValue(valueSmall, { emitEvent: false });
        return;
      }
      if (d.valueSmall && String(d.valueSmall).length < this.numberOfDecimalPlaces) {
        const tmpSmallValue = String(d.valueSmall);
        valueSmall =
          tmpSmallValue.length > this.numberOfDecimalPlaces
            ? tmpSmallValue.slice(-this.numberOfDecimalPlaces)
            : tmpSmallValue;
        this.internalFormGroup.get('valueSmall').setValue(valueSmall, { emitEvent: false });
        return;
      }

      if ((d.valueMain && !this.mainRegex.test(d.valueMain)) || (valueSmall && !this.smallRegex.test(valueSmall))) {
        this.internalFormGroup.setValue(this.previousInternalFormGroupValue, { emitEvent: false });
        return;
      }

      // let moneyValue = Math.abs(Number(d.valueMain ? d.valueMain : 0) * 100) + Number(valueSmall ? valueSmall : 0);
      const moneyValue =
        Math.abs(Number(d.valueMain ? d.valueMain : 0) * 10 ** this.numberOfDecimalPlaces) + Number(valueSmall || 0);

      // moneyValue = d.valueMain && d.valueMain.startsWith('-') ? -moneyValue : moneyValue;

      if (Number.isNaN(moneyValue)) {
        this.setParentFormError();
        return;
      }

      const value: MoneyDTO = {
        currency: d.currency,
        value: moneyValue,
      };
      this.data = value;
      this.formGroup.get(this.fieldConfig.name).setValue(value);
      this.previousInternalFormGroupValue = this.internalFormGroup.getRawValue();

      this.checkIfValid();
    });
  }

  private checkIfValid() {
    if (this.mainValueValid()) {
      this.internalFormGroup.get('valueMain').setErrors(null);
    } else {
      this.internalFormGroup.get('valueMain').setErrors({ incorrect: true }, { emitEvent: true });
    }

    if (this.smallValueValid()) {
      this.internalFormGroup.get('valueSmall').setErrors(null);
    } else {
      this.internalFormGroup.get('valueSmall').setErrors({ incorrect: true });
    }

    if (this.currencyValid()) {
      this.internalFormGroup.get('currency').setErrors(null);
    } else {
      this.internalFormGroup.get('currency').setErrors({ incorrect: true });
    }
    if (this.mainValueValid() && this.smallValueValid() && this.currencyValid()) {
      this.formGroup.get(this.fieldConfig.name).setErrors(null);
    } else {
      this.formGroup.get(this.fieldConfig.name).setErrors({ incorrect: true });
    }
    this.changeDetectorRef.detectChanges();
  }

  private mainValueValid(): boolean {
    return (
      isNotEmpty(this.internalFormGroup.get('valueMain').value) && this.internalFormGroup.get('valueMain').value >= 0
    );
  }

  private smallValueValid(): boolean {
    return (
      isNotEmpty(this.internalFormGroup.get('valueSmall').value) &&
      this.internalFormGroup.get('valueSmall').value >= 0 &&
      `${this.internalFormGroup.get('valueSmall').value}`.length === this.numberOfDecimalPlaces
    );
  }

  private currencyValid(): boolean {
    return isDefined(this.internalFormGroup.get('currency').value);
  }

  private setParentFormError() {
    this.formGroup.get(this.fieldConfig.name).setErrors({ incorrect: true });
  }

  private getValueMain() {
    if (!this.data.value) {
      return null;
    }

    const notRounded = this.data.value / 10 ** this.numberOfDecimalPlaces;
    // const notRounded = this.data.value / 100;
    return String(notRounded >= 0 ? Math.floor(notRounded) : Math.ceil(notRounded));
  }

  shouldDisplaySmallUnit() {
    const { currency } = this.internalFormGroup.getRawValue();
    return currency && currency.decimalPlaces && currency.decimalPlaces >= 1;
  }

  compareFunction(o1: CurrencyDTO, o2: CurrencyDTO) {
    return o1.code === o2.code;
  }
}
